/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */

import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Toast from 'react-native-toast-message';
import { View, ScrollView } from 'react-native';
import { buttonFloating } from '@/assets/styles/buttonStyles';
import SociollaText from '@components/sociolla/SociollaText';
import Colors from '@global/colors';
import Button from '@components/form/Button';
import Input from '@components/form/Input';
import RadioButton from '@components/form/RadioButton';
import * as Layout from '@components/screen/Layout';
import { isWeb } from '@global/screens';
import { showPopup } from '@/store/modal';
import { showAlert } from '@/store/global';

import useApi from '@/hooks/useApi';
import { MsAccount } from '@api/ms-account';

const CustomerTypeFormScreen = (props) => {
  const screenName = 'Customer Type';
  const dispatch = useDispatch();
  const [detailData, setDetailData] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const getCustomerTypeDetail = useApi(MsAccount.getCustomerTypeDetail);

  const initialValue = {
    name: isEdit && detailData?.name ? detailData?.name : '',
    description:
      isEdit && detailData?.description ? detailData?.description : '',
    is_active:
      isEdit && detailData?.is_active !== undefined
        ? detailData?.is_active
        : true,
  };

  const FormValidationSchema = Yup.object().shape({
    name: Yup.string().trim().required('Customer Type Name is required.'),
    description: Yup.string().required('Description is required.'),
    is_active: Yup.boolean().required('Status is required.'),
  });

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setIsEdit(false);
      setDetailData({});
      if (props?.route?.params?.id) {
        setIsEdit(true);
        getDetail(props.route.params.id);
      }
    }
    return () => {
      mounted = false;
    };
  }, [props]);

  const getDetail = async (id) => {
    const response = await getCustomerTypeDetail.request(id);
    if (response?.data?.data && Object.keys(response.data.data).length) {
      setDetailData(response.data.data);
    }
  };

  const submitHandler = async (payload) => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      if (isEdit) {
        const updateData = await MsAccount.updateCustomerType(
          detailData._id,
          payload,
        );
        if (updateData?.data?.data) {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          dispatch(
            showAlert({
              show: true,
              title: 'Changes Saved',
              description: 'Customer type changed successfully.',
              isConfirm: false,
              onCallback: () => {
                props.navigation.navigate('CustomerTypeDetailScreen', {
                  titleScreen: detailData.name,
                  name: detailData.name,
                  id: detailData._id,
                });
              },
            }),
          );
        } else {
          Toast.show({ type: 'error', text1: 'A system error occurred' });
        }
      } else {
        const createData = await MsAccount.createCustomerType(payload);
        if (createData?.data?.data) {
          const result = createData.data.data;
          dispatch(showPopup({ name: 'isLoading', value: false }));
          dispatch(
            showAlert({
              show: true,
              title: 'Customer Type Created',
              description: 'New customer type successfully created.',
              isConfirm: false,
              onCallback: () => {
                if (result._id) {
                  props.navigation.reset({
                    index: 1,
                    routes: [
                      { name: 'CustomerTypeScreen' },
                      {
                        name: 'CustomerTypeDetailScreen',
                        params: {
                          titleScreen: result.name,
                          name: result.name,
                          id: result._id,
                        },
                      },
                    ],
                  });
                } else {
                  props.navigation.reset({
                    index: 0,
                    routes: [
                      {
                        name: 'CustomerTypeScreen',
                      },
                    ],
                  });
                }
              },
            }),
          );
        } else {
          Toast.show({ type: 'error', text1: 'A system error occurred' });
        }
      }
    } catch (error) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({
        type: 'error',
        text1: error?.response?.data?.message || 'A system error occurred',
      });
    }
  };

  const statusOptions = [
    { label: 'Active', value: true },
    { label: 'Inactive', value: false },
  ];

  return (
    <Layout.Container>
      {isEdit && getCustomerTypeDetail?.loading ? (
        <Layout.Wrapper>
          <Layout.Loader />
        </Layout.Wrapper>
      ) : (isEdit ? detailData && Object.keys(detailData).length : true) ? (
        <Formik
          enableReinitialize
          initialValues={initialValue}
          validationSchema={FormValidationSchema}
          onSubmit={(payload) => submitHandler(payload)}>
          {({
            handleChange,
            setFieldValue,
            handleSubmit,
            errors,
            touched,
            values,
          }) => (
            <>
              {isWeb && (
                <Layout.Wrapper white={false}>
                  <Layout.Back
                    title={screenName}
                    onCallback={() =>
                      isEdit
                        ? props.navigation.navigate(
                            'CustomerTypeDetailScreen',
                            props.route.params,
                          )
                        : props.navigation.navigate('CustomerTypeScreen')
                    }
                  />
                  <Layout.Title
                    title={`${isEdit ? 'Edit' : 'Add'} ${screenName}`}>
                    <Button
                      rounded
                      buttonStyle={{ width: 120 }}
                      title={
                        <SociollaText
                          type="bold"
                          style={{
                            fontSize: 14,
                            letterSpacing: 0.5,
                            color: Colors.white,
                          }}>
                          Save
                        </SociollaText>
                      }
                      onPress={handleSubmit}
                    />
                  </Layout.Title>
                </Layout.Wrapper>
              )}
              <Layout.Wrapper rounded isHeight>
                <ScrollView>
                  <Layout.FormWrapper
                    style={{
                      width: isWeb ? 400 : '100%',
                      paddingHorizontal: isWeb ? 20 : 15,
                      paddingTop: 20,
                      marginBottom: 15,
                    }}>
                    <Layout.FormGroup>
                      <Input
                        label="Customer Type Name"
                        value={values.name}
                        isRequired
                        placeholder="Enter Customer Type Name"
                        hasError={
                          errors.name && touched.name ? errors.name : null
                        }
                        onChangeText={handleChange('name')}
                      />
                    </Layout.FormGroup>
                    <Layout.FormGroup>
                      <Input
                        label="Description"
                        value={values.description}
                        isRequired
                        placeholder="Enter Description"
                        hasError={
                          errors.description && touched.description
                            ? errors.description
                            : null
                        }
                        onChangeText={handleChange('description')}
                      />
                    </Layout.FormGroup>
                    <Layout.FormGroup>
                      <RadioButton
                        label="Status"
                        orientation="horizontal"
                        value={values.is_active}
                        data={statusOptions}
                        onChange={(value) => {
                          setFieldValue('is_active', value);
                        }}
                      />
                      {errors.is_active && touched.is_active && (
                        <SociollaText
                          style={{ color: Colors.red, fontSize: 12 }}>
                          {errors.is_active}
                        </SociollaText>
                      )}
                    </Layout.FormGroup>
                  </Layout.FormWrapper>
                </ScrollView>
              </Layout.Wrapper>
              {!isWeb && (
                <View style={buttonFloating}>
                  <Button
                    rounded
                    buttonStyle={{ width: '100%' }}
                    title={
                      <SociollaText
                        type="bold"
                        style={{
                          fontSize: 14,
                          letterSpacing: 0.5,
                          color: Colors.white,
                        }}>
                        Save
                      </SociollaText>
                    }
                    onPress={handleSubmit}
                  />
                </View>
              )}
            </>
          )}
        </Formik>
      ) : null}
    </Layout.Container>
  );
};

export default CustomerTypeFormScreen;
